import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiUrl } from "../../utils/api";
import { cached } from "../../utils/swrCache";

export type ContactScope = "all" | "own" | "team";
export type ContactViewMode = "contacts" | "kundenverbindungen";

type ScopeOption = { kind: "scope"; value: ContactScope; label: string };
type ViewOption = { kind: "view"; value: ContactViewMode; label: string };
type PillOption = ScopeOption | ViewOption;

type Props = {
  scope: ContactScope;
  viewMode: ContactViewMode;
  onScopeChange: (scope: ContactScope) => void;
  onViewModeChange: (mode: ContactViewMode) => void;
};

/**
 * Segmented pill control, right-aligned, plain HTML/CSS (no Fluent buttons).
 *
 * Two responsibilities are folded into one control:
 *
 *  1. Scope of the contact list — role-conditioned ("Alle" / "Persönlich" /
 *     "Team <name>"). Post-BE-3 the scope contract is role-agnostic; pill
 *     visibility is conditioned on what the caller can actually own / belong to:
 *       - "Persönlich"  → only when the caller has ≥1 contact where they are
 *                          FO or BO user (GET /contact/own/count)
 *       - "Team <name>" → only when the caller belongs to ≥1 team
 *
 *  2. View mode — a trailing, always-visible "Kundenverbindungen" pill that
 *     swaps the contact list for the inline Kundenverbindungen table. Because
 *     this pill must be reachable for every role (admins included), the control
 *     always renders at least ["Alle", "Kundenverbindungen"].
 */
export const ContactScopeToggle: React.FC<Props> = ({
  scope,
  viewMode,
  onScopeChange,
  onViewModeChange,
}) => {
  const { isAdmin, roles, teams, user } = useAuth();
  const role = roles?.[0];
  const userId = user?.id ?? null;

  // hasOwn: null while loading, boolean once resolved. Default-hide is the
  // safer UX — showing a pill that returns 0 rows is more confusing than a
  // brief absence.
  const [hasOwn, setHasOwn] = useState<boolean | null>(null);

  useEffect(() => {
    if (isAdmin || role === "service" || !userId) {
      setHasOwn(false);
      return;
    }
    let cancelled = false;
    cached<boolean>(
      `contact.has-own:${userId}`,
      async () => {
        const res = await fetch(apiUrl("contact/own/count"));
        if (!res.ok) throw new Error(`own-count ${res.status}`);
        const data = (await res.json()) as { count: number };
        return (data?.count ?? 0) > 0;
      },
      {
        ttlMs: 5 * 60_000,
        onUpdate: (fresh) => {
          if (!cancelled) setHasOwn(fresh);
        },
      },
    )
      .then((v) => {
        if (!cancelled) setHasOwn(v);
      })
      .catch(() => {
        if (!cancelled) setHasOwn(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, role, userId]);

  // If the toggle is currently on 'own' but the answer says no, fall back
  // to 'all' so the user isn't stuck on an empty scope.
  useEffect(() => {
    if (hasOwn === false && scope === "own") onScopeChange("all");
  }, [hasOwn, scope, onScopeChange]);

  const teamLabel =
    teams && teams.length === 1
      ? `Team ${teams[0].name.replace(/^Team\s+/i, "")}`
      : teams && teams.length > 1
      ? "Meine Teams"
      : null;

  // Scope pills: for non-admin/non-service roles we show "Alle" plus the
  // conditional "Persönlich"/"Team" pills. Admins/service always see "all", so
  // their scope collapses to a single "Alle" pill — which also doubles as the
  // "back to contacts" target next to "Kundenverbindungen".
  const scopeOptions: ScopeOption[] = [
    { kind: "scope", value: "all", label: "Alle" },
  ];
  if (!isAdmin && role !== "service") {
    if (hasOwn) scopeOptions.push({ kind: "scope", value: "own", label: "Persönlich" });
    if (teamLabel) scopeOptions.push({ kind: "scope", value: "team", label: teamLabel });
  }

  const options: PillOption[] = [
    ...scopeOptions,
    { kind: "view", value: "kundenverbindungen", label: "Kundenverbindungen" },
  ];

  const isSelected = (opt: PillOption) =>
    opt.kind === "view"
      ? viewMode === "kundenverbindungen"
      : viewMode === "contacts" && scope === opt.value;

  const handleClick = (opt: PillOption) => {
    if (opt.kind === "view") {
      if (viewMode !== "kundenverbindungen") onViewModeChange("kundenverbindungen");
    } else {
      if (viewMode !== "contacts") onViewModeChange("contacts");
      if (scope !== opt.value) onScopeChange(opt.value);
    }
  };

  return (
    <div
      role="group"
      aria-label="Ansicht und Sichtbarkeitsfilter"
      style={{
        display: "inline-flex",
        backgroundColor: "#f3f2f1",
        border: "1px solid #d1d1d1",
        borderRadius: 8,
        padding: 3,
        gap: 0,
        alignItems: "stretch",
        flexWrap: "wrap",
      }}
    >
      {options.map((opt) => {
        const selected = isSelected(opt);
        return (
          <button
            key={`${opt.kind}:${opt.value}`}
            type="button"
            onClick={() => handleClick(opt)}
            aria-pressed={selected}
            style={{
              minWidth: 110,
              padding: "8px 18px",
              fontSize: 14,
              fontWeight: selected ? 600 : 500,
              color: selected ? "#1f1f1f" : "#444",
              backgroundColor: selected ? "#ffffff" : "transparent",
              border: selected ? "1px solid #c7c7c7" : "1px solid transparent",
              borderRadius: 6,
              cursor: "pointer",
              transition: "background 120ms, color 120ms, font-weight 120ms",
              boxShadow: selected ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              if (!selected) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#e9e8e7";
              }
            }}
            onMouseLeave={(e) => {
              if (!selected) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
              }
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

export default ContactScopeToggle;
